/**
 * SearchBar — Global cross-pillar entity search
 *
 * Debounced input (300ms), results grouped by entity type, keyboard
 * navigation (ArrowDown/Up/Enter/Escape), URL ?q= sync.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ForgeIcon } from '@tylertech/forge-react';
import '@tylertech/forge/icon';
import { useSearchLazy } from '../../graphql/queries';
import type { SearchResult } from '../../graphql/types';
import { SearchableEntityType } from '../../graphql/types';
import { trackEvent } from '../../lib/usageEventBatch';
import './SearchBar.css';

export interface SearchBarProps {
  /** Pre-populate the input for testing / controlled use */
  initialQuery?: string;
}

const ENTITY_TYPE_LABELS: Record<SearchableEntityType, string> = {
  [SearchableEntityType.Service]: 'Services',
  [SearchableEntityType.Team]: 'Teams',
  [SearchableEntityType.BusinessCapability]: 'Capabilities',
  [SearchableEntityType.Technology]: 'Technologies',
  [SearchableEntityType.DataDomain]: 'Data Domains',
};

const ENTITY_TYPE_ORDER: SearchableEntityType[] = [
  SearchableEntityType.Service,
  SearchableEntityType.Team,
  SearchableEntityType.BusinessCapability,
  SearchableEntityType.Technology,
  SearchableEntityType.DataDomain,
];

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

export function SearchBar({ initialQuery }: SearchBarProps): JSX.Element {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { q?: string };

  const [inputValue, setInputValue] = useState<string>(initialQuery ?? search?.q ?? '');
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);

  const [executeSearch, { data, loading, error }] = useSearchLazy();

  const results = data?.search?.results ?? [];

  // Group results by entity type, in display order
  const groupedResults = ENTITY_TYPE_ORDER.reduce<Record<string, SearchResult[]>>((acc, type) => {
    const items = results.filter((r) => r.entityType === type);
    // type is constrained to SearchableEntityType enum — not user-controlled
    // eslint-disable-next-line security/detect-object-injection
    if (items.length > 0) acc[type] = items;
    return acc;
  }, {});

  // Flat ordered list for keyboard nav index
  const flatResults: SearchResult[] = ENTITY_TYPE_ORDER.flatMap(
    // type is constrained to SearchableEntityType enum — not user-controlled
    // eslint-disable-next-line security/detect-object-injection
    (type) => groupedResults[type] ?? []
  );

  const triggerSearch = useCallback(
    (q: string): void => {
      if (q.trim().length < MIN_QUERY_LENGTH) return;
      executeSearch({ variables: { query: q, limit: 20 } });
      setIsOpen(true);
    },
    [executeSearch]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const val = e.target.value;
    setInputValue(val);
    setFocusedIndex(-1);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (val.trim().length < MIN_QUERY_LENGTH) {
      setIsOpen(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      triggerSearch(val);
      // TanStack Router narrows navigate's search type per-route; cast needed for router-agnostic component
      void (navigate as never as (opts: { search: { q: string } }) => void)({ search: { q: val } });
    }, DEBOUNCE_MS);
  };

  const handleResultClick = (result: SearchResult): void => {
    setIsOpen(false);
    setInputValue(result.name);
    trackEvent('feature.search_result_selected', { entityType: result.entityType });
    void navigate({ to: result.url });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!isOpen || flatResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (focusedIndex + 1) % flatResults.length;
      setFocusedIndex(next);
      optionRefs.current.at(next)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = focusedIndex <= 0 ? flatResults.length - 1 : focusedIndex - 1;
      setFocusedIndex(prev);
      optionRefs.current.at(prev)?.focus();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setFocusedIndex(-1);
    }
  };

  const handleOptionKeyDown = (
    e: React.KeyboardEvent<HTMLLIElement>,
    result: SearchResult,
    index: number
  ): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleResultClick(result);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (index + 1) % flatResults.length;
      setFocusedIndex(next);
      optionRefs.current.at(next)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = index <= 0 ? flatResults.length - 1 : index - 1;
      setFocusedIndex(prev);
      optionRefs.current.at(prev)?.focus();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setFocusedIndex(-1);
      inputRef.current?.focus();
    }
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Open dropdown on mount for initialQuery or URL ?q= param
  useEffect(() => {
    if (initialQuery && initialQuery.trim().length >= MIN_QUERY_LENGTH) {
      triggerSearch(initialQuery);
    } else if (!initialQuery && search?.q && search.q.trim().length >= MIN_QUERY_LENGTH) {
      triggerSearch(search.q);
    }
  }, []); // intentional empty deps array for mount-only effect

  // Clear debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const showDropdown = isOpen && inputValue.trim().length >= MIN_QUERY_LENGTH;
  const hasResults = results.length > 0;
  const showError = showDropdown && !loading && !!error;
  const showEmpty = showDropdown && !loading && !error && data !== undefined && !hasResults;

  let flatIndex = 0;

  return (
    <div ref={containerRef} className="search-bar" role="search">
      <div className="search-bar__input-wrapper">
        <ForgeIcon name="search" className="search-bar__icon" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-label="Search entities"
          aria-haspopup="listbox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          placeholder="Search…"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="search-bar__input"
        />
        {loading && (
          <div role="progressbar" aria-label="Searching" className="search-bar__spinner" />
        )}
      </div>

      {showDropdown && (
        <ul role="listbox" aria-label="Search results" className="search-bar__dropdown">
          {showError && (
            <li className="search-bar__error" role="option" aria-selected="false">
              Search unavailable. Check that the API is reachable.
            </li>
          )}

          {showEmpty && (
            <li className="search-bar__empty" role="option" aria-selected="false">
              No results found for &ldquo;{inputValue}&rdquo;
            </li>
          )}

          {ENTITY_TYPE_ORDER.map((type) => {
            // type is constrained to SearchableEntityType enum — not user-controlled
            // eslint-disable-next-line security/detect-object-injection
            const items = groupedResults[type];
            if (!items?.length) return null;

            return (
              <div key={type} className="search-bar__group">
                <li role="presentation" className="search-bar__group-header">
                  {/* type is constrained to SearchableEntityType enum — not user-controlled */}
                  {/* eslint-disable-next-line security/detect-object-injection */}
                  {ENTITY_TYPE_LABELS[type]}
                </li>
                {items.map((result) => {
                  const currentIndex = flatIndex++;
                  return (
                    <li
                      key={result.entityId}
                      ref={(el) => {
                        // currentIndex is a numeric loop counter — not user-controlled
                        // eslint-disable-next-line security/detect-object-injection
                        optionRefs.current[currentIndex] = el;
                      }}
                      role="option"
                      aria-selected={focusedIndex === currentIndex}
                      tabIndex={-1}
                      className={`search-bar__result ${
                        focusedIndex === currentIndex ? 'search-bar__result--focused' : ''
                      }`}
                      onClick={() => handleResultClick(result)}
                      onKeyDown={(e) => handleOptionKeyDown(e, result, currentIndex)}
                    >
                      <span className="search-bar__result-name">{result.name}</span>
                      {result.description && (
                        <span className="search-bar__result-description">{result.description}</span>
                      )}
                    </li>
                  );
                })}
              </div>
            );
          })}
        </ul>
      )}
    </div>
  );
}
